import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { toastStore } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IResponseUser, type IParamsUpdateMember } from 'entities/Users/api/types'
import { EnumUserRoles, UsersApi } from 'entities/Users'
import { User } from 'entities/Users/model/User'
import { RowUser } from 'entities/Users/model/Table/RowUser'
import { MemberPermissionsStore } from 'pages/settings/pages/members/pages/memberPermissions/store/memberPermissionsStore'
import { MemberProfileStore } from 'pages/settings/pages/members/pages/memberProfile/store/memberProfileStore'
import {
  deleteMemberAction,
  logoutAllDevicesAction,
  resetPasswordAction,
} from 'pages/settings/pages/members/ui/actions'
import { MembersPath } from 'pages/settings/pages/members/route/membersPath'
import { downgradeMemberAction } from 'pages/settings/pages/members/ui/actions/downgradeMember/downgradeMember'
import { upgradeMemberAction } from 'pages/settings/pages/members/ui/actions/upgradeMember/upgradeMember'
import { MemberInboxesStore } from 'pages/settings/pages/members/pages/memberInboxes/store/memberInboxesStore'
import { type SettingsStore } from 'pages/settings/store/settingsStore'

export class MemberStore {
  private _memberId: number | null = null
  private _loading = true
  readonly memberPermissionsStore: MemberPermissionsStore
  readonly memberProfileStore: MemberProfileStore
  readonly memberInboxesStore: MemberInboxesStore

  constructor(
    memberId: number | null = null,
    private _entities: IEntitiesStore,
    readonly settings: SettingsStore
  ) {
    this._memberId = memberId
    this.memberPermissionsStore = new MemberPermissionsStore(this._entities)
    this.memberProfileStore = new MemberProfileStore(this._entities)
    this.memberInboxesStore = new MemberInboxesStore(this._entities, this.settings)

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get hasMemberProfileChanges() {
    if (!this.member) return false

    return (
      this.memberProfileStore.firstName !== this.member.first_name ||
      this.memberProfileStore.lastName !== this.member.last_name ||
      (this.memberProfileStore.timezone &&
        this.memberProfileStore.timezone !== this.member.timezone)
    )
  }

  get member() {
    return this._entities.usersStore.getItem(this._memberId)
  }

  get paramsUpdateMemberProfile(): IParamsUpdateMember | null {
    if (!this.member) return null

    return {
      last_name: this.memberProfileStore.lastName,
      email: this.member.email,
      timezone: this.memberProfileStore.timezone,
      role: this.member.role,
      permissions: this.member instanceof User ? this.member.permissions : null,
      first_name: this.memberProfileStore.firstName,
    }
  }

  get isMyProfile() {
    return this.member?.id === this._entities.usersStore.user?.id
  }

  get isOwner() {
    return this.member?.role === EnumUserRoles.OWNER
  }

  init = async () => {
    if (!this._memberId) return

    try {
      runInAction(() => {
        this._loading = true
      })

      await Promise.all([
        this._entities.usersStore.initPromise,
        this._entities.usersStore.initMembersPromise,
        this._entities.permissionsStore.init(),
        this._entities.inboxesStore.initPromise,
        this._entities.inboxesStore.organizationInboxesInitPromise,
      ])

      const member = await this._entities.usersStore.getItemById(this._memberId)

      runInAction(() => {
        if (member) {
          this.memberInboxesStore.setMemberId(member.id)
          this.memberProfileStore.init(member)

          let permissions = null
          let useOrganizationSettings = false

          if (member instanceof User) {
            permissions = member.permissions
            useOrganizationSettings = member.useOrganizationSettings
          }
          this.memberPermissionsStore.init(member.role, permissions, useOrganizationSettings)
        }
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  onSaveMemberProfile = async () => {
    if (!this.hasMemberProfileChanges || !this.paramsUpdateMemberProfile || !this.member) return

    try {
      const member = this.member
      const params = this.paramsUpdateMemberProfile

      const { data } = await UsersApi.updateMember(member.id, params)
      if (data && member instanceof User) {
        if (member.first_name !== data.first_name) {
          toastStore.add({
            type: 'success',
            title: "Member's first name updated",
          })
        }

        if (member.last_name !== data.last_name) {
          toastStore.add({
            type: 'success',
            title: "Member's last name updated",
          })
        }

        if (member.timezone !== data.timezone) {
          toastStore.add({
            type: 'success',
            title: "Member's timezone updated",
          })
        }

        member.syncOrigin(data)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  handleSuccessUpdateMemberPermissions = (member: IResponseUser) => {
    const { role, permissions, use_organization_settings } = member

    this.memberPermissionsStore.init(role, permissions, use_organization_settings)
  }

  onSaveMemberPermissions = async () => {
    if (
      !this.memberPermissionsStore.role ||
      !this.memberPermissionsStore.permissions ||
      !this.memberPermissionsStore.hasChanges ||
      this.memberPermissionsStore.loading ||
      !this.memberPermissionsStore.paramsUpdatePermissions ||
      !this.member
    ) {
      return
    }

    this.memberPermissionsStore.setLoading(true)

    const member = this.member
    const params = this.memberPermissionsStore.paramsUpdatePermissions

    if (
      this.memberPermissionsStore.paramsUpdatePermissions.role === EnumUserRoles.VIEW_ONLY &&
      member.role !== EnumUserRoles.VIEW_ONLY &&
      member instanceof User
    ) {
      this.memberPermissionsStore.setLoading(false)
      downgradeMemberAction(
        member,
        this._entities,
        this.memberPermissionsStore.permissions,
        this.memberPermissionsStore.useOrganizationSettings,
        this.handleSuccessUpdateMemberPermissions
      )

      return
    }

    if (
      this.memberPermissionsStore.paramsUpdatePermissions.role !== EnumUserRoles.VIEW_ONLY &&
      member.role === EnumUserRoles.VIEW_ONLY &&
      member instanceof User &&
      !this._entities.subscriptionStore.seatsAvailable
    ) {
      this.memberPermissionsStore.setLoading(false)
      upgradeMemberAction(
        member,
        this._entities,
        this.memberPermissionsStore.paramsUpdatePermissions,
        this.handleSuccessUpdateMemberPermissions
      )

      return
    }

    try {
      const { data } = await UsersApi.updateMember(member.id, params)

      if (data && member instanceof User) {
        member.syncOrigin(data)
        const { role, permissions, useOrganizationSettings } = member
        this.memberPermissionsStore.init(role, permissions, useOrganizationSettings)

        toastStore.add({
          type: 'success',
          title: 'Permissions updated',
        })
      }
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this.memberPermissionsStore.setLoading(false)
      })
    }
  }

  handleResetPassword = () => {
    if (!this.member) return

    resetPasswordAction([this.member])
  }

  handleLogoutAllDevices = () => {
    if (!this.member) return

    logoutAllDevicesAction([this.member])
  }

  handleDeleteMember = () => {
    if (!this.member) return

    const rowMember = new RowUser(this.member as User, this._entities)

    deleteMemberAction(rowMember, this._entities, () => {
      uiStore.changeRoute({ path: `/${MembersPath.root}` })
    })
  }

  handleTimezone = (value: string) => {
    if (!this.member) return

    this.memberProfileStore.setTimezone(value)
    this.onSaveMemberProfile()
  }

  reset = () => {
    this._memberId = null
    this._loading = true
    this.memberProfileStore.reset()
    this.memberPermissionsStore.reset()
    this.memberInboxesStore.reset()
  }
}
